/**
 * Share-to-X composition — specs/share-to-x.md.
 *
 * Everything here is pure string math so the 280-character budget is
 * unit-testable. X counts any URL as exactly T_CO_LENGTH characters and the
 * web intent appends the `url` param after a separator, so the text budget is
 * fixed regardless of the real link's length.
 */

export const POST_LIMIT = 280;
/** Every URL costs exactly this on X (t.co wrapping), plus one separator. */
export const T_CO_LENGTH = 23;
/** Below this many characters, a quote isn't worth posting a title over. */
const MIN_QUOTE_ROOM = 60;
const ELLIPSIS = "…";

export interface ShareSource {
  bookTitle: string | null;
  videoUrl: string;
}

export interface ComposedPost {
  /** Contents of the intent's `text` parameter. */
  text: string;
  /** Contents of the intent's `url` parameter ("" when the source has none). */
  url: string;
  truncated: boolean;
}

/** Collapse rendered-DOM selection text: trim, normalize inner whitespace,
 * keep paragraph breaks. */
export function cleanSelection(raw: string): string {
  return raw
    .replace(/\r/g, "")
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max - ELLIPSIS.length);
  const lastSpace = slice.lastIndexOf(" ");
  // a pathologically long single word still truncates, just mid-word
  return (lastSpace > max * 0.5 ? slice.slice(0, lastSpace) : slice).trimEnd() + ELLIPSIS;
}

/**
 * Assemble quote + attribution within the 280 budget.
 * Priority: quote > URL > title — the title is dropped before the quote is
 * squeezed below MIN_QUOTE_ROOM; the URL is never dropped (attribution to the
 * creator is part of the product's ethic).
 */
export function formatQuote(selection: string, source: ShareSource): ComposedPost {
  const quote = cleanSelection(selection);
  const urlCost = source.videoUrl ? T_CO_LENGTH + 1 : 0;
  const budget = POST_LIMIT - urlCost;

  const title = (source.bookTitle ?? "").trim();
  // curly quotes around the passage + "\n\n- " before the title
  const quoted = (q: string) => `“${q}”`;
  const attribution = title ? `\n\n- ${title}` : "";
  const overheadWithTitle = 2 + attribution.length;

  let text: string;
  let truncated = false;

  if (title && budget - overheadWithTitle >= MIN_QUOTE_ROOM) {
    const room = budget - overheadWithTitle;
    const fitted = truncateAtWord(quote, room);
    truncated = fitted !== quote;
    text = quoted(fitted) + attribution;
  } else {
    const room = budget - 2;
    const fitted = truncateAtWord(quote, room);
    truncated = fitted !== quote;
    text = quoted(fitted);
  }

  return { text, url: source.videoUrl, truncated };
}

export function intentUrl(post: ComposedPost): string {
  const params = new URLSearchParams({ text: post.text });
  if (post.url) params.set("url", post.url);
  return `https://x.com/intent/post?${params.toString()}`;
}

/** The clipboard variant: same content, real URL inline. */
export function clipboardText(post: ComposedPost): string {
  return post.url ? `${post.text}\n${post.url}` : post.text;
}
